/**
 * Preferințe client-side (UX, nu sensibile) — persistate în localStorage.
 * Nu pune aici chei cripto sau tokeni.
 */

const REVEAL_KEY = 'cb_reveal_timeout_sec';
const REVEAL_DEFAULT = 30;
const REVEAL_MIN = 5;
const REVEAL_MAX = 300;

export const REVEAL_TIMEOUT_LIMITS = {
  min: REVEAL_MIN,
  max: REVEAL_MAX,
  default: REVEAL_DEFAULT,
};

function clampReveal(n: number): number {
  return Math.max(REVEAL_MIN, Math.min(REVEAL_MAX, Math.floor(n)));
}

export function getRevealTimeout(): number {
  if (typeof window === 'undefined') return REVEAL_DEFAULT;
  const raw = window.localStorage.getItem(REVEAL_KEY);
  if (!raw) return REVEAL_DEFAULT;
  const n = Number(raw);
  if (!Number.isFinite(n)) return REVEAL_DEFAULT;
  return clampReveal(n);
}

export function setRevealTimeout(sec: number): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(REVEAL_KEY, String(clampReveal(sec)));
  window.dispatchEvent(new CustomEvent('cb-preferences-changed', { detail: { key: REVEAL_KEY } }));
}

// ==========================================================
// Background palette + pattern — aplicat pe main via data-attr
// ==========================================================

export type BgPalette = 'purple' | 'mint' | 'cyan' | 'rose' | 'amber' | 'ocean';
export type BgPattern = 'dots' | 'grid' | 'graph' | 'crosshatch' | 'diagonal' | 'none';

const BG_PALETTE_KEY = 'cb_bg_palette';
const BG_PATTERN_KEY = 'cb_bg_pattern';

const VALID_PALETTES: BgPalette[] = ['purple', 'mint', 'cyan', 'rose', 'amber', 'ocean'];
const VALID_PATTERNS: BgPattern[] = [
  'dots',
  'grid',
  'graph',
  'crosshatch',
  'diagonal',
  'none',
];

export function getBgPalette(): BgPalette {
  if (typeof window === 'undefined') return 'purple';
  const raw = window.localStorage.getItem(BG_PALETTE_KEY);
  return VALID_PALETTES.includes(raw as BgPalette) ? (raw as BgPalette) : 'purple';
}

export function setBgPalette(v: BgPalette): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(BG_PALETTE_KEY, v);
  applyBgAttrs();
  window.dispatchEvent(new CustomEvent('cb-preferences-changed', { detail: { key: BG_PALETTE_KEY } }));
}

export function getBgPattern(): BgPattern {
  if (typeof window === 'undefined') return 'dots';
  const raw = window.localStorage.getItem(BG_PATTERN_KEY);
  return VALID_PATTERNS.includes(raw as BgPattern) ? (raw as BgPattern) : 'dots';
}

export function setBgPattern(v: BgPattern): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(BG_PATTERN_KEY, v);
  applyBgAttrs();
  window.dispatchEvent(new CustomEvent('cb-preferences-changed', { detail: { key: BG_PATTERN_KEY } }));
}

/**
 * Aplică data-attr-urile pe <html> pentru ca CSS-ul să reacționeze.
 * Folosit la boot + după set.
 */
export function applyBgAttrs(): void {
  if (typeof window === 'undefined') return;
  const html = document.documentElement;
  html.setAttribute('data-bg-palette', getBgPalette());
  html.setAttribute('data-bg-pattern', getBgPattern());
}
